import type { ThreeElements } from '@react-three/fiber'
import { useDetalhesFinos, useNivelQualidade } from '../ambiente/contextoQualidade'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { PIVO_CORTADOR } from './geometriaCortador'
import {
  DESVIO_LAMINA,
  ESPESSURA_LAMINA,
  ESPESSURA_PLACA_CABECA,
  malhasCortador,
  type MalhasCortador,
} from './malhasCortador'

interface Props extends Omit<ThreeElements['group'], 'children'> {
  anguloMandibula?: number
  ativa?: boolean
}

const LARANJA = '#ea580c'
const LARANJA_ATIVO = '#f97316'
const ACO_LAMINA = '#cbd5e1'
const GUME_POLIDO = '#f8fafc'
const PRETO_CABECA = '#1b1d22'
const PRETO_CARCACA = '#22252b'
const PRETO_BATERIA = '#17181c'
const VERDE_LED = '#22c55e'

const Z_PLACA = DESVIO_LAMINA + ESPESSURA_LAMINA / 2 + 0.004 + ESPESSURA_PLACA_CABECA / 2
const Z_FACE_PLACA = Z_PLACA + ESPESSURA_PLACA_CABECA / 2

/** Lâmina superior, no referencial do pivô, num plano z acima do médio. */
function Lamina({ malhas, abertura }: { malhas: MalhasCortador; abertura: number }) {
  return (
    <group rotation={[0, 0, abertura]} position={[0, 0, DESVIO_LAMINA]}>
      <mesh geometry={malhas.lamina} castShadow>
        <meshStandardMaterial color={ACO_LAMINA} metalness={0.85} roughness={0.28} />
      </mesh>
      <mesh geometry={malhas.faixaGume}>
        <meshStandardMaterial color={GUME_POLIDO} metalness={0.95} roughness={0.12} />
      </mesh>
    </group>
  )
}

/**
 * Cortador hidráulico a bateria, inspirado no Holmatro série Pentheon.
 * Guarda a silhueta e as cores (laranja e preto), sem logo nem texto.
 *
 * - Lâminas curvas em bico de papagaio, extrudadas com chanfro, com o fio
 *   polido em faixa; cruzam como tesoura no fechamento, em planos separados;
 * - cabeça de duas placas com abas defletoras, envolvendo o pino do pivô;
 * - colar preto, alça de transporte 360° e cilindro laranja torneado;
 * - carcaça do motor com bateria no topo e manopla traseira com anéis.
 *
 * No nível `baixa` saem as peças miúdas e as curvas ficam mais grossas.
 */
export function CortadorHolmatro3D({ anguloMandibula = 0.25, ativa = false, ...props }: Props) {
  const nivel = useNivelQualidade()
  const detalhes = useDetalhesFinos()
  const malhas = malhasCortador(nivel)
  const [px, py] = PIVO_CORTADOR

  return (
    <group {...props}>
      {/* 1. Lâminas. A inferior é o espelho da superior em y; o espelho também
          leva o plano z para o outro lado e inverte o sentido da abertura. */}
      <group position={[px, py, 0]}>
        <Lamina malhas={malhas} abertura={anguloMandibula} />
        <group scale={[1, -1, -1]}>
          <Lamina malhas={malhas} abertura={anguloMandibula} />
        </group>

        {/* Pino do pivô e tampas com rebaixo da porca */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.03, 0.03, Z_FACE_PLACA * 2 + 0.006, 24]} />
          <meshStandardMaterial color="#e2e8f0" metalness={0.92} roughness={0.18} />
        </mesh>
        {detalhes &&
          [1, -1].map((lado) => (
            <mesh key={lado} position={[0, 0, lado * (Z_FACE_PLACA + 0.005)]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.02, 0.02, 0.006, 6]} />
              <meshStandardMaterial color="#94a3b8" metalness={0.88} roughness={0.25} />
            </mesh>
          ))}
      </group>

      {/* 2. Cabeça: placas laterais com abas defletoras e o bloco entre elas */}
      {[1, -1].map((lado) => (
        <mesh key={lado} geometry={malhas.placaCabeca} position={[0, 0, lado * Z_PLACA]} castShadow>
          <meshStandardMaterial color={PRETO_CABECA} metalness={0.4} roughness={0.5} />
        </mesh>
      ))}
      <mesh position={[0.285, 0, 0]}>
        <CaixaArredondada args={[0.07, 0.075, Z_PLACA * 2]} />
        <meshStandardMaterial color={PRETO_CABECA} metalness={0.4} roughness={0.5} />
      </mesh>

      {/* Colar entre o cilindro e a cabeça */}
      <mesh geometry={malhas.colar}>
        <meshStandardMaterial color={PRETO_CABECA} metalness={0.45} roughness={0.42} />
      </mesh>

      {/* 3. Alça de transporte 360°, em volta do colar */}
      <group position={[0.215, 0, 0]}>
        <mesh rotation={[0, Math.PI / 2, 0]} castShadow>
          <torusGeometry args={[0.085, 0.012, nivel === 'baixa' ? 8 : 14, nivel === 'baixa' ? 24 : 40]} />
          <meshStandardMaterial color="#1c2430" roughness={0.6} metalness={0.3} />
        </mesh>
        {[1, -1].map((lado) => (
          <mesh key={lado} position={[0, lado * 0.064, 0]}>
            <cylinderGeometry args={[0.011, 0.011, 0.034, 12]} />
            <meshStandardMaterial color="#0f172a" roughness={0.5} />
          </mesh>
        ))}
      </group>

      {/* 4. Cilindro hidráulico laranja */}
      <mesh geometry={malhas.cilindro} castShadow>
        <meshStandardMaterial color={ativa ? LARANJA_ATIVO : LARANJA} metalness={0.25} roughness={0.42} />
      </mesh>

      {/* 5. Carcaça do motor, bateria no topo e manopla traseira */}
      <group position={[-0.24, 0, 0]}>
        <mesh castShadow>
          <CaixaArredondada args={[0.18, 0.12, 0.125]} raio={0.02} />
          <meshStandardMaterial color={PRETO_CARCACA} roughness={0.55} />
        </mesh>
        {detalhes &&
          [-0.04, 0, 0.04].map((z) => (
            <mesh key={z} position={[-0.07, -0.03, z]}>
              <CaixaArredondada args={[0.045, 0.012, 0.01]} />
              <meshStandardMaterial color="#0b0d10" roughness={0.85} />
            </mesh>
          ))}

        <group position={[-0.02, 0.09, 0]}>
          <mesh castShadow>
            <CaixaArredondada args={[0.13, 0.062, 0.088]} raio={0.012} />
            <meshStandardMaterial color={PRETO_BATERIA} roughness={0.65} />
          </mesh>
          {/* Trilho de encaixe da bateria */}
          <mesh position={[0, -0.03, 0]}>
            <boxGeometry args={[0.12, 0.004, 0.092]} />
            <meshStandardMaterial color={LARANJA} roughness={0.45} />
          </mesh>
          {detalhes && (
            <mesh position={[0.035, 0.032, 0]}>
              <CaixaArredondada args={[0.018, 0.003, 0.03]} />
              <meshStandardMaterial color={VERDE_LED} emissive={VERDE_LED} emissiveIntensity={0.8} />
            </mesh>
          )}
        </group>
      </group>

      <mesh geometry={malhas.manopla} castShadow>
        <meshStandardMaterial color="#111215" roughness={0.75} />
      </mesh>
    </group>
  )
}
